import * as React from 'react'
import { connect } from 'react-redux'

import {
  applicationStepLoaded,
  applicationStepSelected,
  fetchCityStateFromZip,
  fetchZipcodeFromIP,
  filterChanged,
  ratesFiltersCleared,
  ratesFilterSet,
  stepSelected,
  updateLead,
} from 'common/actions'
import {
  applicationFieldBlurred,
  applicationFieldChanged,
  applicationFieldRegistered,
  applicationFieldShowValidationStatus,
  applicationFieldUnregistered,
  applicationUpdateValidationStatus,
} from 'common/actions/application'

import {
  Application,
  ApplicationStep,
  CoApplicantAddressMatch,
  Field,
  FieldDataValue,
  FieldKey,
  LOAN_PURPOSE_LIST,
  LoanOfferProductSubType,
  LoanRatesFilter,
  PlaidStatus,
  SLREducationLevelOptions,
} from 'embed/constants'
import { Fields } from 'embed/constants/fields'
import {
  LoanPlaidIntegration,
  PlaidSummaryCardSingleStep,
  StepByStepPlaidSummaryCard,
  withExperiment,
} from 'embed/experiments'

import { creditScoreOfferUrlSelector } from 'embed/selectors'
import {
  loanApplicationFieldsSelector,
  loanApplicationPurposeFieldSelector,
  loanApplicationSelector,
  loanPurposeIsAutomobileRefinanceSelector,
  loanPurposeIsSLRSelector,
} from 'embed/selectors/application'
import {
  configCompanyUuidSelector,
  configFeatureFlagsSelector,
  configShouldAllowCoApplicantSelector,
  configStateSelector,
} from 'embed/selectors/config'
import { filtersProductSelector } from 'embed/selectors/filters'
import { getBool, getBooleanFromBooleanString } from 'embed/utils'
import { MultiProductState } from 'embed/reducers'
import { uiHasCalledGeocoderSelector, uiShowCoApplicantAddressFieldsSelector } from 'embed/selectors/ui'
import {
  BooleanOptions,
  ConfigKeys,
  Filter,
  LoanFlow,
  LoanPurposeOptions,
  LoanStep,
  Product,
  VehicleIdTypeOptions,
} from 'even-ts-static'
import LoanApplicationView from './index'
import SingleStepWithSummaryLoanApplicationView from './SingleStepWithSummary'
import { ConnectedStepbystepApplicationContainer as StepbystepApplicationContainer } from './Stepbystep/container'
import StudentLoanRefinanceApplicationView from './StudentLoanRefinance'
import { Auto, BasicLoan, equals, Loan, SLR } from './loan-variant'

interface StoreProps {
  application: Application
  companyUuid: string
  consent: boolean
  consentValidationStatus: boolean
  creditScoreOfferUrl: string | undefined
  directReferral: boolean
  featureFlags: string[]
  hasCalledGeocoder: boolean
  hasCoApplicant: boolean
  hideLoanPurpose: boolean
  loanPurpose: FieldDataValue
  loanVariant: Loan
  plaidStatus: PlaidStatus
  selectedPlaidIntegrationVariantId?: string
  selectedPlaidSummaryCardSingleStepVariantId?: string
  selectedStepByStepPlaidSummaryCardVariantId?: string
  shouldAllowLoanCoApplicant: boolean
  showCoApplicantAddressFields: boolean
  SLREducationLevel: FieldDataValue | undefined
  stepbystepApplication: boolean
  zipcode: FieldDataValue | undefined
}

interface DispatchProps {
  dispatchApplicationFieldBlurred: (field: Field) => void
  dispatchApplicationFieldChanged: (field: Field, value: unknown) => void
  dispatchApplicationFieldUnregistered: (field: Field) => void
  dispatchApplicationFieldRegistered: (field: Field) => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
  dispatchApplicationLoaded: () => void
  dispatchApplicationStepSelected: (applicationStep: ApplicationStep) => void
  dispatchApplicationUpdateValidationStatus: () => void
  dispatchBackToStepOne: () => void
  dispatchConsentsToFCRAandTCPA: () => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchFetchCityStateFromZip: (zipcode: string) => void
  dispatchRatesFiltersCleared: (filterType: LoanRatesFilter, filterValue: unknown) => void
  dispatchRatesFilterSet: (filterType: LoanRatesFilter, filterValue: unknown) => void
  dispatchToggledCoapplicant: (value: string) => void
  dispatchUpdateLead: () => void
}

export type LoanApplicationContainerProps = DispatchProps & StoreProps

export class LoanApplicationContainer extends React.Component<LoanApplicationContainerProps> {
  constructor(props) {
    super(props)

    if (props.hasCalledGeocoder === false) {
      this.props.dispatchFetchZipcodeFromIP()
    } else if (props.zipcode !== undefined) {
      props.dispatchFetchCityStateFromZip(props.zipcode)
    }
  }

  componentDidMount() {
    this.props.dispatchUpdateLead()
    this.props.dispatchApplicationLoaded()
  }

  onCoApplicantToggleClick = (event: string): void => {
    this.props.dispatchToggledCoapplicant(event)

    if (!getBooleanFromBooleanString(event)) {
      this.removeCoapplicantInfoFields()

      if (this.props.showCoApplicantAddressFields) {
        this.removeCoapplicantAddressFields()
      }
    }
    this.props.dispatchApplicationUpdateValidationStatus()
  }
  handleVehicleIdTypeChangeHandler = (fields?: Field[], value?: VehicleIdTypeOptions) => {
    const { dispatchApplicationFieldChanged, dispatchApplicationFieldUnregistered } = this.props
    fields?.forEach((field) => dispatchApplicationFieldUnregistered(field))
    dispatchApplicationFieldChanged(Fields.VehicleIdType, value)
  }

  coApplicantAddressOnChange = (field: Field, event: string) => {
    if (event === CoApplicantAddressMatch.SameAsApplicant) {
      this.removeCoapplicantAddressFields()
    }
    this.onChange(field, event)
  }

  removeCoapplicantInfoFields = () => {
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantFirstName)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantLastName)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantDateOfBirth)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantAnnualIncomeLoan)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantAddressMatch)
  }

  removeCoapplicantAddressFields = () => {
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantAddressOne)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantAddressTwo)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantCity)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantState)
    this.props.dispatchApplicationFieldUnregistered(Fields.CoApplicantZipcode)
  }

  onConsentChange = (event) => {
    this.props.dispatchApplicationFieldChanged(Fields.ConsentsToFCRA, event.target.checked)
    this.props.dispatchApplicationFieldBlurred(Fields.ConsentsToFCRA)
    this.props.dispatchApplicationFieldChanged(Fields.ConsentsToTCPA, event.target.checked)
    this.props.dispatchApplicationFieldBlurred(Fields.ConsentsToTCPA)
  }

  onChange = (field: Field, value) => {
    this.props.dispatchApplicationFieldChanged(field, value)
  }

  loanPurposeOnChange = (value: FieldDataValue) => {
    if (value === LoanPurposeOptions.STUDENT_REFINANCE) {
      // when changing to SLR
      this.props.dispatchApplicationFieldUnregistered(Fields.Education)
      this.onCoApplicantToggleClick(BooleanOptions.False)
      this.props.dispatchRatesFilterSet(LoanRatesFilter.ProductSubType, LoanOfferProductSubType.StudentLoanRefinance)
    }
    this.props.dispatchApplicationFieldChanged(Fields.LoanPurpose, value)
  }

  slrLoanPurposeOnChange = (value: FieldDataValue) => {
    // when changing away from SLR
    this.props.dispatchApplicationFieldChanged(Fields.LoanPurpose, value)
    this.props.dispatchApplicationFieldUnregistered(Fields.Education)
    this.props.dispatchApplicationFieldUnregistered(Fields.University)
    this.props.dispatchApplicationFieldUnregistered(Fields.GraduateDegree)
    this.props.dispatchRatesFiltersCleared(LoanRatesFilter.ProductSubType, LoanOfferProductSubType.StudentLoanRefinance)
  }

  slrEducationLevelOnChange = (value: FieldDataValue) => {
    if (
      this.props.SLREducationLevel === SLREducationLevelOptions.DOCTORATE &&
      value !== SLREducationLevelOptions.DOCTORATE
    ) {
      this.props.dispatchApplicationFieldUnregistered(Fields.GraduateDegree)
    }
    this.props.dispatchApplicationFieldChanged(Fields.Education, value)
  }

  render() {
    const { stepbystepApplication } = this.props
    const applicationViewProps = {
      coApplicantAddressOnChange: this.coApplicantAddressOnChange,
      displayGraduateLevel: this.props.SLREducationLevel === SLREducationLevelOptions.DOCTORATE,
      handleConsentsToFCRAandTCPA: this.props.dispatchConsentsToFCRAandTCPA,
      handleVehicleIdTypeChangeHandler: this.handleVehicleIdTypeChangeHandler,
      loanPurposeOnChange: this.loanPurposeOnChange,
      onChange: this.onChange,
      onCoApplicantToggleClick: this.onCoApplicantToggleClick,
      onConsentChange: this.onConsentChange,
      slrEducationLevelOnChange: this.slrEducationLevelOnChange,
      slrLoanPurposeOnChange: this.slrLoanPurposeOnChange,
      ...this.props,
    }

    const isInSingleStepSummaryExperiment =
      this.props.selectedPlaidSummaryCardSingleStepVariantId === PlaidSummaryCardSingleStep.variants.withSummaryCard.id

    // If we're in the StepByStepPlaidSummaryCard experiment, force them through the step by step flow
    if (
      this.props.selectedStepByStepPlaidSummaryCardVariantId ===
      StepByStepPlaidSummaryCard.variants.withPlaidSummaryCard.id
    ) {
      return <StepbystepApplicationContainer {...applicationViewProps} />
    }

    if (stepbystepApplication) {
      return <StepbystepApplicationContainer {...applicationViewProps} />
    } else if (equals(this.props.loanVariant, SLR())) {
      return <StudentLoanRefinanceApplicationView {...applicationViewProps} />
    } else if (isInSingleStepSummaryExperiment && this.props.plaidStatus === PlaidStatus.Success) {
      return <SingleStepWithSummaryLoanApplicationView {...applicationViewProps} />
    } else {
      return <LoanApplicationView {...applicationViewProps} />
    }
  }
}

function getLoanVariant(state: MultiProductState) {
  if (loanPurposeIsSLRSelector(state)) {
    return SLR()
  } else if (loanPurposeIsAutomobileRefinanceSelector(state)) {
    return Auto()
  } else {
    return BasicLoan()
  }
}

const mapStateToProps = (state: MultiProductState): StoreProps => {
  const loanApplicationFields = loanApplicationFieldsSelector(state) ?? {}
  return {
    application: loanApplicationSelector(state) as Application,
    companyUuid: configCompanyUuidSelector(state) ?? '',
    consent:
      !!loanApplicationFields[Filter.ConsentsToFCRA]?.value && !!loanApplicationFields[Filter.ConsentsToTCPA]?.value,
    consentValidationStatus:
      !!loanApplicationFields[Filter.ConsentsToFCRA]?.shouldShowValidationStatus ||
      !!loanApplicationFields[Filter.ConsentsToTCPA]?.shouldShowValidationStatus,
    creditScoreOfferUrl: creditScoreOfferUrlSelector(state),
    directReferral: state.config[ConfigKeys.Step] === LoanStep.Application,
    featureFlags: configFeatureFlagsSelector(state),
    hasCalledGeocoder: uiHasCalledGeocoderSelector(state),
    hasCoApplicant: getBool(filtersProductSelector(state, Product.Loan)?.[Filter.HasCoApplicant]),
    hideLoanPurpose: (configStateSelector(state)[ConfigKeys.loanPurposeOptions] ?? LOAN_PURPOSE_LIST).length === 1,
    loanPurpose: loanApplicationPurposeFieldSelector(state)?.value,
    loanVariant: getLoanVariant(state),
    plaidStatus: state.ui?.plaid?.status,
    shouldAllowLoanCoApplicant: configShouldAllowCoApplicantSelector(state),
    showCoApplicantAddressFields: uiShowCoApplicantAddressFieldsSelector(state),
    SLREducationLevel: loanApplicationFields[Filter.EducationLevel]?.value,
    stepbystepApplication: state.config[ConfigKeys.LoanFlow] === LoanFlow.StepbystepApplication,
    zipcode: loanApplicationFields[Filter.Zipcode]?.value,
  }
}
const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchApplicationFieldBlurred: (field: Field) => dispatch(applicationFieldBlurred(Product.Loan, field.key)),
  dispatchApplicationFieldChanged: (field: Field, value: string | number) =>
    dispatch(applicationFieldChanged(Product.Loan, field.key, value)),
  dispatchApplicationFieldRegistered: (field: Field) => dispatch(applicationFieldRegistered(Product.Loan, field.key)),
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) =>
    dispatch(applicationFieldShowValidationStatus(Product.Loan, fieldKey, show)),
  dispatchApplicationFieldUnregistered: (field: Field) =>
    dispatch(applicationFieldUnregistered(Product.Loan, field.key)),
  dispatchApplicationLoaded: () => dispatch(applicationStepLoaded()),
  dispatchApplicationStepSelected: (applicationStep: ApplicationStep) =>
    dispatch(applicationStepSelected(Product.Loan, applicationStep)),
  dispatchApplicationUpdateValidationStatus: () => dispatch(applicationUpdateValidationStatus(Product.Loan)),
  dispatchBackToStepOne: () => dispatch(stepSelected(Product.Loan, LoanStep.OfferList)),
  dispatchConsentsToFCRAandTCPA: () => {
    dispatch(applicationFieldChanged(Product.Loan, Fields.ConsentsToFCRA.key, true))
    dispatch(applicationFieldBlurred(Product.Loan, Fields.ConsentsToFCRA.key))
    dispatch(applicationFieldChanged(Product.Loan, Fields.ConsentsToTCPA.key, true))
    dispatch(applicationFieldBlurred(Product.Loan, Fields.ConsentsToTCPA.key))
  },
  dispatchFetchCityStateFromZip: (zipcode) => dispatch(fetchCityStateFromZip(Product.Loan, zipcode)),
  dispatchFetchZipcodeFromIP: () => dispatch(fetchZipcodeFromIP(Product.Loan, false)),
  dispatchRatesFiltersCleared: (filterType: LoanRatesFilter, filterValue: unknown) =>
    dispatch(ratesFiltersCleared(Product.Loan, filterType, filterValue)),
  dispatchRatesFilterSet: (filterType: LoanRatesFilter, filterValue: unknown) =>
    dispatch(ratesFilterSet(Product.Loan, filterType, filterValue)),
  dispatchToggledCoapplicant: (value: string) =>
    dispatch(filterChanged(Product.Loan, Filter.HasCoApplicant, value, { userInitiated: true })),
  dispatchUpdateLead: () => dispatch(updateLead(Product.Loan)),
})

export const ConnectedApplicationContainer = connect<StoreProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(LoanApplicationContainer)

export default withExperiment(StepByStepPlaidSummaryCard, {
  selectedVariantPropName: 'selectedStepByStepPlaidSummaryCardVariantId',
})(
  withExperiment(PlaidSummaryCardSingleStep, {
    selectedVariantPropName: 'selectedPlaidSummaryCardSingleStepVariantId',
  })(
    withExperiment(LoanPlaidIntegration, {
      selectedVariantPropName: 'selectedPlaidIntegrationVariantId',
    })(ConnectedApplicationContainer),
  ),
)
